/**
 * Admin Panel API Endpoints
 * Mock endpoints for Bookings, Categories, Services, Settings, and Content
 */

import { apiClient, servicesClient } from './client';
import type {
  BookingsResponse,
  BookingFilters,
  CategoriesResponse,
  Category,
  CategoryFormData,
  ServicesResponse,
  Service,
  ServiceFormData,
  Settings,
  SettingsFormData,
  ContentResponse,
  Content,
  ContentFormData,
} from './adminTypes';

export const adminAPI = {
  // Bookings Management
  bookings: {
    getBookings: (page: number, perPage: number, filters?: BookingFilters) =>
      apiClient.get<BookingsResponse>('api/bookings', {
        page,
        perPage,
        ...filters,
      }),

    getBookingById: (id: string) => apiClient.get(`api/bookings/${id}`),

    deleteBooking: (id: string) => apiClient.delete(`api/bookings/${id}`),
  },

  // Categories Management
  categories: {
    getCategories: (limit: number = 10, offset: number = 0, search?: string, status?: string) =>
      servicesClient.get<CategoriesResponse>('admin/category', {
        limit,
        offset,
        search: search || '',
        ...(status && { status }),
      }),

    getCategoryById: (id: string) => apiClient.get<Category>(`api/category/${id}`),

    createCategory: (data: CategoryFormData) => apiClient.post<Category>('api/category', data),

    updateCategory: (id: string, data: Partial<CategoryFormData>) =>
      apiClient.patch<Category>(`api/category/${id}`, data),

    deleteCategory: (id: string) => apiClient.delete(`api/category/${id}`),
  },

  // Services Management
  services: {
    getServices: (
      limit: number = 10,
      offset: number = 0,
      search?: string,
      categoryId?: string,
      status?: string,
      shortBy?: string,
      shortType?: string
    ) =>
      servicesClient.get<ServicesResponse>('admin/category-services', {
        limit,
        offset,
        search: search || '',
        categoryId: categoryId || '',
        status: status || '',
        ...(shortBy && { shortBy }),
        ...(shortType && { shortType }),
      }),

    getServiceById: (id: string) => servicesClient.get<Service>(`admin/category-services/${id}`),

    createService: (data: ServiceFormData) =>
      servicesClient.post<Service>('category-services', data),

    updateService: (id: string, data: Partial<ServiceFormData>) =>
      servicesClient.patch<Service>(`category-services/${id}`, data),

    deleteService: (id: string) => servicesClient.delete(`category-services/${id}`),
  },

  // Settings Management
  settings: {
    getSettings: () => apiClient.get<Settings>('/api/settings'),

    updateSettings: (data: Partial<SettingsFormData>) =>
      apiClient.patch<Settings>('/api/settings', data),
  },

  // Content Management
  content: {
    getContent: () => apiClient.get<ContentResponse>('api/content'),

    getContentById: (id: string) => apiClient.get<Content>(`api/content/${id}`),

    updateContent: (id: string, data: Partial<ContentFormData>) =>
      apiClient.patch<Content>(`api/content/${id}`, data),

    uploadImage: (file: File) => {
      const formData = new FormData();
      formData.append('image', file);
      return apiClient.post<{ url: string }>('api/content/upload', formData);
    },
  },
};
